import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navbar from './components/Navbar';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Sales from './pages/Sales';
import Expenses from './pages/Expenses';
import PettyCash from './pages/PettyCash';
import Reports from './pages/Reports';
import AuditLogs from './pages/AuditLogs';
import Users from './pages/Users';
import Stores from './pages/Stores';
import Notifications from './pages/Notifications';
import Help from './pages/Help';
import DevicePending from './pages/DevicePending';
import Devices from './pages/Devices';
import Suppliers from './pages/Suppliers';
import AnnualReports from './pages/AnnualReports';
import Permissions from './pages/Permissions';
import PosCommissionsReport from './pages/PosCommissionsReport';
import MonthlySalesReport from './pages/MonthlySalesReport';
import AdminTools from './pages/AdminTools';
import AuditTransactions from './pages/AuditTransactions';
import Cashbook from './pages/Cashbook';
import ComparativeReports from './pages/ComparativeReports';
import DataRecovery from './pages/DataRecovery';
import './App.css';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Cargando...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Si se especifican roles permitidos, verificar que el usuario tenga uno de esos roles
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
            <h2 className="text-2xl font-bold text-red-800 mb-2">Acceso Denegado</h2>
            <p className="text-red-700">No tienes permisos para acceder a esta sección.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        {children}
      </main>
    </div>
  );
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Cargando...</div>
      </div>
    );
  }

  if (user) {
    // admin_assistant goes to cashbook instead of dashboard
    if (user.role === 'admin_assistant') {
      return <Navigate to="/cashbook" />;
    }
    return <Navigate to="/dashboard" />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/device-pending" element={<DevicePending />} />
          <Route path="/dashboard" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor', 'store_manager', 'cashier', 'purchase_manager']}><Dashboard /></ProtectedRoute>} />
          <Route path="/sales" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor', 'admin_assistant', 'store_manager', 'cashier']}><Sales /></ProtectedRoute>} />
          <Route path="/expenses" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor', 'admin_assistant', 'store_manager', 'cashier']}><Expenses /></ProtectedRoute>} />
          <Route path="/cashbook" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor', 'admin_assistant', 'store_manager', 'cashier']}><Cashbook /></ProtectedRoute>} />
          <Route path="/petty-cash" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor', 'admin_assistant', 'store_manager', 'cashier']}><PettyCash /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor', 'admin_assistant', 'store_manager']}><Reports /></ProtectedRoute>} />
          <Route path="/annual-reports" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor', 'admin_assistant', 'store_manager', 'purchase_manager']}><AnnualReports /></ProtectedRoute>} />
          <Route path="/pos-commissions" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'admin_assistant']}><PosCommissionsReport /></ProtectedRoute>} />
          <Route path="/monthly-sales" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'admin_assistant']}><MonthlySalesReport /></ProtectedRoute>} />
          <Route path="/audit" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor', 'admin_assistant', 'store_manager']}><AuditLogs /></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute allowedRoles={['admin']}><Users /></ProtectedRoute>} />
          <Route path="/permissions" element={<ProtectedRoute allowedRoles={['admin']}><Permissions /></ProtectedRoute>} />
          <Route path="/stores" element={<ProtectedRoute allowedRoles={['admin']}><Stores /></ProtectedRoute>} />
          <Route path="/suppliers" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor', 'admin_assistant', 'store_manager', 'purchase_manager']}><Suppliers /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
          <Route path="/devices" element={<ProtectedRoute allowedRoles={['admin']}><Devices /></ProtectedRoute>} />
          <Route path="/admin-tools" element={<ProtectedRoute allowedRoles={['admin']}><AdminTools /></ProtectedRoute>} />
          <Route path="/audit-transactions" element={<ProtectedRoute allowedRoles={['admin']}><AuditTransactions /></ProtectedRoute>} />
          <Route path="/comparative-reports" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'supervisor']}><ComparativeReports /></ProtectedRoute>} />
          <Route path="/data-recovery" element={<ProtectedRoute allowedRoles={['admin']}><DataRecovery /></ProtectedRoute>} />
          <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
          <Route path="/" element={<Navigate to="/dashboard" />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;